import { useEffect, useState, useRef, useCallback } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Minimize,
  Settings,
  Loader2,
  Subtitles,
  Share2,
  Cast,
  Check,
  SkipBack,
  SkipForward,
  Radio,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CustomVideoPlayer = ({
  channelId,
  hlsUrl,
  hlsQualities,
  isLive = false,
  autoPlay = true,
  onReady,
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hlsRef = useRef(null);
  const hideControlsTimeout = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showQualityMenu, setShowQualityMenu] = useState(false);
  const [currentQuality, setCurrentQuality] = useState("auto");
  const [availableQualities, setAvailableQualities] = useState([]);
  const [codecError, setCodecError] = useState(null);
  
  // Closed Captions State
  const [captionsEnabled, setCaptionsEnabled] = useState(false);
  const [currentCaption, setCurrentCaption] = useState("");
  const [showCaptionMenu, setShowCaptionMenu] = useState(false);
  const [captionStyle, setCaptionStyle] = useState("default"); // default, large, yellow
  
  // Share & Cast State
  const [showShareToast, setShowShareToast] = useState(false);
  const [isCasting, setIsCasting] = useState(false);
  const [castAvailable, setCastAvailable] = useState(false);
  
  // DVR State (for live streams with DVR window)
  const [dvrEnabled, setDvrEnabled] = useState(false);
  const [liveEdge, setLiveEdge] = useState(0);
  const [seekableStart, setSeekableStart] = useState(0);
  const [isBehindLive, setIsBehindLive] = useState(false);

  // Caption Styles
  const captionStyles = {
    default: "text-white text-base md:text-lg bg-black/80 px-3 py-1.5",
    large: "text-white text-xl md:text-2xl bg-black/80 px-4 py-2 font-bold",
    yellow: "text-yellow-300 text-base md:text-lg bg-black/80 px-3 py-1.5",
    outline: "text-white text-base md:text-lg px-3 py-1.5 [text-shadow:_2px_2px_0_#000,_-2px_2px_0_#000,_2px_-2px_0_#000,_-2px_-2px_0_#000]",
  };

  // Quality options based on hlsQualities prop
  const qualityOptions = [
    { label: "Auto", value: "auto", url: hlsQualities?.source || hlsUrl },
    { label: "Source", value: "source", url: hlsQualities?.source || hlsUrl },
    { label: "1080p", value: "1080p", url: hlsQualities?.["1080p"] },
    { label: "720p", value: "720p", url: hlsQualities?.["720p"] },
    { label: "480p", value: "480p", url: hlsQualities?.["480p"] },
    { label: "360p", value: "360p", url: hlsQualities?.["360p"] },
  ].filter((q) => q.url);

  // Initialize HLS player
  useEffect(() => {
    const initHls = async () => {
      if (!hlsUrl || !videoRef.current) return;

      try {
        const Hls = (await import("hls.js")).default;

        if (Hls.isSupported()) {
          if (hlsRef.current) {
            hlsRef.current.destroy();
          }

          const hls = new Hls({
            enableWorker: true,
            lowLatencyMode: isLive,
            backBufferLength: isLive ? 30 : 90,
            // More robust settings for browser compatibility
            debug: true, // Enable debug for troubleshooting
            manifestLoadingTimeOut: 20000,
            manifestLoadingMaxRetry: 4,
            levelLoadingTimeOut: 20000,
            fragLoadingTimeOut: 20000,
            // Disable dynamic codec detection that causes issues
            stretchShortVideoTrack: true,
            // Force fMP4 container for better compatibility
            enableSoftwareAES: true,
            // Allow partial segments
            appendErrorMaxRetry: 5,
            // Configure SourceBuffer handling
            maxBufferLength: 30,
            maxMaxBufferLength: 60,
            maxBufferSize: 60 * 1000 * 1000, // 60MB
            maxBufferHole: 0.5,
            // Workaround for codec detection issues
            renderTextTracksNatively: true,
          });

          hlsRef.current = hls;

          // Use source quality as default, fallback to main hlsUrl
          const streamUrl =
            currentQuality === "auto"
              ? `${BACKEND_URL}${hlsQualities?.source || hlsUrl}`
              : `${BACKEND_URL}${hlsQualities?.[currentQuality] || hlsUrl}`;

          hls.loadSource(streamUrl);
          hls.attachMedia(videoRef.current);

          hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
            setAvailableQualities(data.levels.map((l) => l.height));
            setIsLoading(false);
            if (autoPlay) {
              videoRef.current.play().catch(() => {});
            }
            onReady?.();
          });

          // Handle subtitle/caption tracks
          hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, (event, data) => {
            console.log("Subtitle tracks available:", data.subtitleTracks);
          });

          hls.on(Hls.Events.SUBTITLE_TRACK_LOADED, (event, data) => {
            console.log("Subtitle track loaded:", data);
          });

          hls.on(Hls.Events.ERROR, (event, data) => {
            console.error("HLS error:", data.type, data.details, data);
            if (data.fatal) {
              console.error("HLS fatal error:", data);
              
              // Check for codec support issues
              if (data.details === "bufferAddCodecError") {
                setCodecError("Your browser doesn't support the required video codecs (H.264/AAC). Please try a different browser like Chrome, Firefox, or Safari.");
                setIsLoading(false);
                return;
              }
              
              switch (data.type) {
                case Hls.ErrorTypes.NETWORK_ERROR:
                  // Try to recover from network error
                  console.log("Fatal network error, trying to recover...");
                  hls.startLoad();
                  break;
                case Hls.ErrorTypes.MEDIA_ERROR:
                  // Only try to recover if it's not a codec error (max 2 retries)
                  if (!window.__hlsRecoveryAttempts) window.__hlsRecoveryAttempts = 0;
                  window.__hlsRecoveryAttempts++;
                  
                  if (window.__hlsRecoveryAttempts <= 2) {
                    console.log(`Fatal media error, trying to recover (attempt ${window.__hlsRecoveryAttempts})...`);
                    hls.recoverMediaError();
                  } else {
                    console.log("Max recovery attempts reached");
                    setCodecError("Unable to play video. Your browser may not support the required codecs.");
                    setIsLoading(false);
                  }
                  break;
                default:
                  // Cannot recover, destroy and reload
                  console.log("Cannot recover, destroying HLS instance");
                  hls.destroy();
                  setIsLoading(false);
                  break;
              }
            }
          });
        } else if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
          // Safari native HLS
          videoRef.current.src = `${BACKEND_URL}${hlsUrl}`;
          videoRef.current.addEventListener("loadedmetadata", () => {
            setIsLoading(false);
            if (autoPlay) {
              videoRef.current.play().catch(() => {});
            }
            onReady?.();
          });
        }
      } catch (e) {
        console.error("HLS init error:", e);
        setIsLoading(false);
      }
    };

    initHls();

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, [hlsUrl, currentQuality, isLive, autoPlay, onReady, hlsQualities]);

  // Handle native text tracks (WebVTT captions)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCueChange = () => {
      const tracks = video.textTracks;
      for (let i = 0; i < tracks.length; i++) {
        const track = tracks[i];
        if (track.kind === 'captions' || track.kind === 'subtitles') {
          track.mode = captionsEnabled ? 'showing' : 'hidden';
          
          if (captionsEnabled && track.activeCues && track.activeCues.length > 0) {
            const cue = track.activeCues[0];
            setCurrentCaption(cue.text);
          }
        }
      }
    };

    // Listen for track changes
    video.textTracks.addEventListener('change', handleCueChange);
    
    // Update caption visibility when toggle changes
    handleCueChange();

    return () => {
      video.textTracks.removeEventListener('change', handleCueChange);
    };
  }, [captionsEnabled]);

  // Simulated auto-captions for demo (when no real caption track exists)
  // This would be replaced with actual speech-to-text service in production
  useEffect(() => {
    if (!captionsEnabled || !isPlaying) {
      setCurrentCaption("");
      return;
    }

    // Check if video has actual caption tracks
    const video = videoRef.current;
    if (video && video.textTracks.length > 0) {
      // Use native captions
      return;
    }

    // Demo: Simulate auto-captions for live streams
    // In production, this would connect to a speech-to-text service
    if (isLive) {
      const sampleCaptions = [
        "Live sports coverage brought to you by Antler Sports Network",
        "Stay tuned for game updates and highlights",
        "Follow us for more high school and college sports",
        "",
      ];
      let index = 0;

      const captionInterval = setInterval(() => {
        setCurrentCaption(sampleCaptions[index % sampleCaptions.length]);
        index++;
      }, 4000);

      return () => clearInterval(captionInterval);
    }
  }, [captionsEnabled, isPlaying, isLive]);

  // Video event handlers
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
      if (video.buffered.length > 0) {
        setBuffered(video.buffered.end(video.buffered.length - 1));
      }
      
      // DVR detection for live streams
      if (isLive && video.seekable && video.seekable.length > 0) {
        const seekStart = video.seekable.start(video.seekable.length - 1);
        const seekEnd = video.seekable.end(video.seekable.length - 1);
        const dvrWindow = seekEnd - seekStart;
        
        // Enable DVR if we have at least 30 seconds of seekable content
        if (dvrWindow > 30) {
          setDvrEnabled(true);
          setSeekableStart(seekStart);
          setLiveEdge(seekEnd);
          // Consider behind live if more than 10 seconds from edge
          setIsBehindLive(seekEnd - video.currentTime > 10);
        }
      }
    };
    const handleDurationChange = () => setDuration(video.duration);
    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };
    const handleWaiting = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);

    video.addEventListener("play", handlePlay);
    video.addEventListener("pause", handlePause);
    video.addEventListener("timeupdate", handleTimeUpdate);
    video.addEventListener("durationchange", handleDurationChange);
    video.addEventListener("volumechange", handleVolumeChange);
    video.addEventListener("waiting", handleWaiting);
    video.addEventListener("canplay", handleCanPlay);

    return () => {
      video.removeEventListener("play", handlePlay);
      video.removeEventListener("pause", handlePause);
      video.removeEventListener("timeupdate", handleTimeUpdate);
      video.removeEventListener("durationchange", handleDurationChange);
      video.removeEventListener("volumechange", handleVolumeChange);
      video.removeEventListener("waiting", handleWaiting);
      video.removeEventListener("canplay", handleCanPlay);
    };
  }, []);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Controls - defined as useCallback for keyboard shortcuts
  const togglePlay = useCallback(() => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
      } else {
        videoRef.current.pause();
      }
    }
  }, []);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
    }
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  const toggleCaptions = useCallback(() => {
    setCaptionsEnabled(prev => !prev);
  }, []);

  // DVR Controls
  const goToLive = useCallback(() => {
    if (videoRef.current && dvrEnabled) {
      videoRef.current.currentTime = liveEdge;
      setIsBehindLive(false);
    }
  }, [dvrEnabled, liveEdge]);

  const skipBackDvr = useCallback((seconds = 10) => {
    if (videoRef.current && dvrEnabled) {
      const newTime = Math.max(seekableStart, videoRef.current.currentTime - seconds);
      videoRef.current.currentTime = newTime;
    }
  }, [dvrEnabled, seekableStart]);

  const skipForwardDvr = useCallback((seconds = 10) => {
    if (videoRef.current && dvrEnabled) {
      const newTime = Math.min(liveEdge, videoRef.current.currentTime + seconds);
      videoRef.current.currentTime = newTime;
    }
  }, [dvrEnabled, liveEdge]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle shortcuts when video player is focused or in fullscreen
      const container = containerRef.current;
      if (!container) return;
      
      // Check if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      switch (e.key.toLowerCase()) {
        case ' ':
        case 'k':
          // Space or K = Play/Pause
          e.preventDefault();
          togglePlay();
          break;
        case 'arrowleft':
          // Left arrow = Seek backward 10s (supports DVR for live)
          e.preventDefault();
          if (videoRef.current) {
            if (dvrEnabled && isLive) {
              skipBackDvr(10);
            } else if (!isLive) {
              videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
            }
          }
          break;
        case 'arrowright':
          // Right arrow = Seek forward 10s (supports DVR for live)
          e.preventDefault();
          if (videoRef.current) {
            if (dvrEnabled && isLive) {
              skipForwardDvr(10);
            } else if (!isLive) {
              videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 10);
            }
          }
          break;
        case 'arrowup':
          // Up arrow = Increase volume
          e.preventDefault();
          if (videoRef.current) {
            videoRef.current.volume = Math.min(1, videoRef.current.volume + 0.1);
            videoRef.current.muted = false;
          }
          break;
        case 'arrowdown':
          // Down arrow = Decrease volume
          e.preventDefault();
          if (videoRef.current) {
            videoRef.current.volume = Math.max(0, videoRef.current.volume - 0.1);
          }
          break;
        case 'm':
          // M = Mute/Unmute
          e.preventDefault();
          toggleMute();
          break;
        case 'f':
          // F = Toggle fullscreen
          e.preventDefault();
          toggleFullscreen();
          break;
        case 'c':
          // C = Toggle captions
          e.preventDefault();
          toggleCaptions();
          break;
        case 'j':
          // J = Seek backward 10s (YouTube style)
          e.preventDefault();
          if (videoRef.current && !isLive) {
            videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - 10);
          }
          break;
        case 'l':
          // L = Seek forward 10s (YouTube style)
          e.preventDefault();
          if (videoRef.current && !isLive) {
            videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + 10);
          }
          break;
        case '0':
        case '1':
        case '2':
        case '3':
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
          // Number keys = Jump to percentage of video
          e.preventDefault();
          if (videoRef.current && !isLive) {
            const percent = parseInt(e.key) / 10;
            videoRef.current.currentTime = duration * percent;
          }
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isLive, duration, togglePlay, toggleMute, toggleFullscreen, toggleCaptions]);

  // Auto-hide controls
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    if (isPlaying) {
      hideControlsTimeout.current = setTimeout(() => {
        setShowControls(false);
        setShowQualityMenu(false);
        setShowCaptionMenu(false);
      }, 3000);
    }
  }, [isPlaying]);

  const handleMouseMove = () => resetControlsTimeout();
  const handleMouseLeave = () => {
    if (isPlaying) {
      setShowControls(false);
      setShowQualityMenu(false);
      setShowCaptionMenu(false);
    }
  };

  // Controls
  const handleVolumeSliderChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      videoRef.current.muted = newVolume === 0;
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current && !isLive) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      videoRef.current.currentTime = percent * duration;
    }
  };

  const handleQualityChange = (quality) => {
    setCurrentQuality(quality);
    setShowQualityMenu(false);
  };

  // Share functionality
  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 2000);
    }
  };

  // Cast functionality (Chromecast/AirPlay)
  const handleCast = async () => {
    // Check for native casting support
    if (videoRef.current) {
      // Try AirPlay (Safari)
      if (videoRef.current.webkitShowPlaybackTargetPicker) {
        videoRef.current.webkitShowPlaybackTargetPicker();
        return;
      }
      
      // Try Remote Playback API (Chrome)
      if (videoRef.current.remote) {
        try {
          await videoRef.current.remote.prompt();
          setIsCasting(true);
        } catch (err) {
          console.log("Cast cancelled or unavailable");
        }
        return;
      }
      
      // Fallback: Show alert with instructions
      alert("To cast this video:\n\n• Chrome: Click the three dots menu → Cast\n• Safari: Use AirPlay from the video controls\n• Smart TV: Open this URL on your TV's browser");
    }
  };

  // Check for cast availability
  useEffect(() => {
    if (videoRef.current) {
      // Check for AirPlay
      if (videoRef.current.webkitShowPlaybackTargetPicker) {
        setCastAvailable(true);
      }
      // Check for Remote Playback API
      if (videoRef.current.remote) {
        videoRef.current.remote.watchAvailability((available) => {
          setCastAvailable(available);
        }).catch(() => {
          // Remote playback not supported
          setCastAvailable(true); // Still show button for manual casting
        });
      } else {
        setCastAvailable(true); // Show button anyway for instructions
      }
    }
  }, []);

  const formatTime = (seconds) => {
    if (!isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-video bg-black group"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-testid="custom-video-player"
    >
      {/* Video Element */}
      <video
        ref={videoRef}
        className="w-full h-full"
        playsInline
        onClick={togglePlay}
        crossOrigin="anonymous"
      >
        {/* Caption track placeholder - would be populated from stream */}
        <track kind="captions" src="" label="English" srcLang="en" default />
      </video>

      {/* Codec Error Message */}
      {codecError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/90 z-50">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-600/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-white text-lg font-bold mb-2">Playback Error</h3>
            <p className="text-zinc-400 text-sm mb-4">{codecError}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-zinc-200 transition-colors"
            >
              Reload Page
            </button>
          </div>
        </div>
      )}

      {/* Loading Spinner */}
      {isLoading && !codecError && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/50">
          <Loader2 className="w-12 h-12 text-white animate-spin" />
        </div>
      )}

      {/* Live Badge */}
      {isLive && (
        <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white text-xs font-black uppercase tracking-widest z-20">
          <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
          LIVE
        </div>
      )}

      {/* Share Button - Top Right */}
      <button
        onClick={handleShare}
        className={`absolute top-4 ${isLive ? 'left-24' : 'left-4'} flex items-center gap-2 px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-xs font-bold uppercase tracking-wider z-20 transition-colors`}
        data-testid="share-button"
      >
        <Share2 className="w-4 h-4" />
        Share
      </button>

      {/* Share Toast */}
      {showShareToast && (
        <div className="absolute top-16 left-4 flex items-center gap-2 px-3 py-2 bg-green-600 text-white text-xs font-bold z-30 animate-fade-in">
          <Check className="w-4 h-4" />
          Link copied to clipboard!
        </div>
      )}

      {/* Closed Captions Display */}
      {captionsEnabled && currentCaption && (
        <div 
          className="absolute bottom-20 left-0 right-0 flex justify-center pointer-events-none z-30"
          data-testid="caption-display"
        >
          <div className={`max-w-[80%] text-center rounded ${captionStyles[captionStyle]}`}>
            {currentCaption}
          </div>
        </div>
      )}

      {/* Controls Overlay */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 ${
          showControls ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        data-testid="player-controls"
      >
        {/* Center Play Button */}
        <button
          className="absolute inset-0 flex items-center justify-center"
          onClick={togglePlay}
          data-testid="center-play-btn"
        >
          {!isPlaying && (
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center hover:bg-white/30 transition-colors">
              <Play className="w-10 h-10 text-white ml-1" fill="white" />
            </div>
          )}
        </button>

        {/* Bottom Controls Bar */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          {/* Progress Bar (for VOD or DVR-enabled live streams) */}
          {((!isLive && duration > 0) || (isLive && dvrEnabled)) && (
            <div
              className="relative h-1 bg-white/20 mb-4 cursor-pointer group/progress"
              onClick={(e) => {
                if (isLive && dvrEnabled) {
                  // DVR seeking for live
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  const dvrWindow = liveEdge - seekableStart;
                  const newTime = seekableStart + (percent * dvrWindow);
                  if (videoRef.current) {
                    videoRef.current.currentTime = newTime;
                  }
                } else {
                  handleSeek(e);
                }
              }}
              data-testid="progress-bar"
            >
              {/* Buffered */}
              <div
                className="absolute h-full bg-white/30"
                style={{ 
                  width: isLive && dvrEnabled 
                    ? `${((buffered - seekableStart) / (liveEdge - seekableStart)) * 100}%`
                    : `${(buffered / duration) * 100}%` 
                }}
              />
              {/* Progress */}
              <div
                className="absolute h-full bg-red-600"
                style={{ 
                  width: isLive && dvrEnabled
                    ? `${((currentTime - seekableStart) / (liveEdge - seekableStart)) * 100}%`
                    : `${(currentTime / duration) * 100}%` 
                }}
              />
              {/* Thumb */}
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-red-600 rounded-full opacity-0 group-hover/progress:opacity-100 transition-opacity"
                style={{ 
                  left: isLive && dvrEnabled
                    ? `${((currentTime - seekableStart) / (liveEdge - seekableStart)) * 100}%`
                    : `${(currentTime / duration) * 100}%` 
                }}
              />
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            {/* Left Controls */}
            <div className="flex items-center gap-3">
              {/* DVR Skip Back Button (for live with DVR) */}
              {isLive && dvrEnabled && (
                <button
                  onClick={() => skipBackDvr(10)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="skip-back-btn"
                  title="Skip back 10 seconds"
                >
                  <SkipBack className="w-5 h-5 text-white" />
                </button>
              )}
              
              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="play-pause-btn"
              >
                {isPlaying ? (
                  <Pause className="w-6 h-6 text-white" />
                ) : (
                  <Play className="w-6 h-6 text-white" fill="white" />
                )}
              </button>
              
              {/* DVR Skip Forward Button (for live with DVR) */}
              {isLive && dvrEnabled && (
                <button
                  onClick={() => skipForwardDvr(10)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="skip-forward-btn"
                  title="Skip forward 10 seconds"
                >
                  <SkipForward className="w-5 h-5 text-white" />
                </button>
              )}

              {/* Volume */}
              <div className="flex items-center gap-2 group/volume">
                <button
                  onClick={toggleMute}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  data-testid="mute-btn"
                >
                  {isMuted || volume === 0 ? (
                    <VolumeX className="w-5 h-5 text-white" />
                  ) : (
                    <Volume2 className="w-5 h-5 text-white" />
                  )}
                </button>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeSliderChange}
                  className="w-0 group-hover/volume:w-20 transition-all duration-200 accent-red-600"
                  data-testid="volume-slider"
                />
              </div>

              {/* Time */}
              {!isLive && (
                <span className="text-white text-sm font-mono" data-testid="time-display">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
              )}
              
              {/* DVR Time Display & Go Live Button (for live with DVR) */}
              {isLive && dvrEnabled && (
                <div className="flex items-center gap-2">
                  <span className="text-white text-sm font-mono" data-testid="dvr-time-display">
                    -{formatTime(liveEdge - currentTime)}
                  </span>
                  {isBehindLive && (
                    <button
                      onClick={goToLive}
                      className="flex items-center gap-1.5 px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wider rounded transition-colors"
                      data-testid="go-live-btn"
                    >
                      <Radio className="w-3 h-3" />
                      Go Live
                    </button>
                  )}
                </div>
              )}
              
              {/* Live Badge (for live without DVR or at live edge) */}
              {isLive && !isBehindLive && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-red-600/20 text-red-500 text-xs font-bold uppercase tracking-wider rounded">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
                  Live
                </div>
              )}
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-2">
              {/* Closed Captions Toggle */}
              <div className="relative">
                <button
                  onClick={toggleCaptions}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setShowCaptionMenu(!showCaptionMenu);
                  }}
                  className={`p-2 hover:bg-white/10 rounded-lg transition-colors ${
                    captionsEnabled ? 'bg-white/20' : ''
                  }`}
                  data-testid="captions-btn"
                  title={captionsEnabled ? "Turn off captions" : "Turn on captions"}
                >
                  <Subtitles className={`w-5 h-5 ${captionsEnabled ? 'text-red-500' : 'text-white'}`} />
                </button>

                {/* Caption Style Menu */}
                {showCaptionMenu && (
                  <div
                    className="absolute bottom-full right-0 mb-2 bg-zinc-900 border border-white/10 rounded-lg overflow-hidden min-w-[140px]"
                    data-testid="caption-menu"
                  >
                    <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-zinc-500 border-b border-white/10">
                      Caption Style
                    </div>
                    {[
                      { id: 'default', label: 'Default' },
                      { id: 'large', label: 'Large' },
                      { id: 'yellow', label: 'Yellow' },
                      { id: 'outline', label: 'Outline' },
                    ].map((style) => (
                      <button
                        key={style.id}
                        onClick={() => {
                          setCaptionStyle(style.id);
                          setShowCaptionMenu(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                          captionStyle === style.id
                            ? "text-red-500 bg-white/5"
                            : "text-white"
                        }`}
                      >
                        {style.label}
                        {captionStyle === style.id && (
                          <span className="float-right">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Quality Selector */}
              <div className="relative">
                <button
                  onClick={() => setShowQualityMenu(!showQualityMenu)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors flex items-center gap-1"
                  data-testid="quality-btn"
                >
                  <Settings className="w-5 h-5 text-white" />
                  <span className="text-white text-xs uppercase hidden sm:inline">
                    {currentQuality === "auto" ? "Auto" : currentQuality}
                  </span>
                </button>

                {/* Quality Menu */}
                {showQualityMenu && (
                  <div
                    className="absolute bottom-full right-0 mb-2 bg-zinc-900 border border-white/10 rounded-lg overflow-hidden min-w-[120px]"
                    data-testid="quality-menu"
                  >
                    {qualityOptions.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => handleQualityChange(option.value)}
                        className={`w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors ${
                          currentQuality === option.value
                            ? "text-red-500 bg-white/5"
                            : "text-white"
                        }`}
                        data-testid={`quality-option-${option.value}`}
                      >
                        {option.label}
                        {currentQuality === option.value && (
                          <span className="float-right">✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Cast Button */}
              <button
                onClick={handleCast}
                className={`p-2 hover:bg-white/10 rounded-lg transition-colors ${isCasting ? 'bg-white/20' : ''}`}
                data-testid="cast-btn"
                title="Cast to TV"
              >
                <Cast className={`w-5 h-5 ${isCasting ? 'text-blue-500' : 'text-white'}`} />
              </button>

              {/* Fullscreen */}
              <button
                onClick={toggleFullscreen}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                data-testid="fullscreen-btn"
              >
                {isFullscreen ? (
                  <Minimize className="w-5 h-5 text-white" />
                ) : (
                  <Maximize className="w-5 h-5 text-white" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomVideoPlayer;
